import type { WarehouseGroup } from './Summary'

export type PlaceWarehouses = Record<string, Record<string, WarehouseGroup>>

export interface ListRow {
  key: string
  id?: string
  number?: string
  hash: string
  warehouseTypeId: string
  group: WarehouseGroup
  isAvailableNow: boolean
  availableFrom?: string
  isAvailable: boolean
}

/** Klucz wybranej grupy (tryb listy bez konkretnych magazynów) */
export const groupKey = (warehouseTypeId: string, hash: string) => `${warehouseTypeId}::${hash}`

export const parseGroupKey = (key?: string) => {
  if (!key) return undefined
  const [warehouseTypeId, hash] = key.split('::')
  return warehouseTypeId && hash ? { warehouseTypeId, hash } : undefined
}

/** for_rent: brak klucza -> niedostępny, null -> dostępny od teraz, string -> dostępny od daty */
export const getWarehouseAvailability = (group: WarehouseGroup, id: string) => {
  const value = group.for_rent?.[id]
  return {
    isAvailableNow: value === null,
    availableFrom: typeof value === 'string' ? value : undefined,
    isAvailable: value !== undefined,
  }
}

export const getGroupAvailability = (group: WarehouseGroup) => {
  const forRentDates = Object.values(group.for_rent || {}).filter((date): date is string => typeof date === 'string')
  return {
    isAvailableNow: !!group.isAvailableForRentNow,
    availableFrom: group.isAvailableForRentNow ? undefined : forRentDates[0],
    isAvailable: !!group.isAvailableForRentNow || forRentDates.length > 0,
  }
}

const filterGroups = (warehouses: PlaceWarehouses | undefined, filters?: basic.Form) => {
  const typeFilter: string | undefined = filters?.attributes?.warehouseTypeId
  const hashFilter: string | undefined = filters?.attributes?.hash
  const result: Array<{ warehouseTypeId: string, hash: string, group: WarehouseGroup }> = []
  Object.entries(warehouses || {}).forEach(([warehouseTypeId, groups]) => {
    if (typeFilter && typeFilter !== warehouseTypeId) return
    Object.entries(groups || {}).forEach(([hash, group]) => {
      if (hashFilter && typeFilter && hashFilter !== hash) return
      result.push({ warehouseTypeId, hash, group })
    })
  })
  return result
}

const rowRank = (row: ListRow) => row.isAvailableNow ? 0 : row.isAvailable ? 1 : 2

export const getListRows = (warehouses: PlaceWarehouses | undefined, filters: basic.Form | undefined, listAllWarehouses: boolean): ListRow[] => {
  const groups = filterGroups(warehouses, filters)
  const rows: ListRow[] = []

  groups.forEach(({ warehouseTypeId, hash, group }) => {
    if (listAllWarehouses) {
      Object.entries(group.warehousesInfo || {}).forEach(([id, info]) => {
        rows.push({
          key: id,
          id,
          number: info?.number,
          hash,
          warehouseTypeId,
          group,
          ...getWarehouseAvailability(group, id),
        })
      })
    } else {
      rows.push({
        key: groupKey(warehouseTypeId, hash),
        hash,
        warehouseTypeId,
        group,
        ...getGroupAvailability(group),
      })
    }
  })

  return rows.sort((a, b) => {
    const rank = rowRank(a) - rowRank(b)
    if (rank !== 0) return rank
    if (a.number !== undefined && b.number !== undefined) {
      return a.number.localeCompare(b.number, undefined, { numeric: true })
    }
    return (a.group.order ?? 0) - (b.group.order ?? 0)
  })
}

/** Szuka grupy, do której należy magazyn o podanym id */
export const findGroupByWarehouseId = (warehouses: PlaceWarehouses | undefined, id: string) => {
  for (const [warehouseTypeId, groups] of Object.entries(warehouses || {})) {
    for (const [hash, group] of Object.entries(groups || {})) {
      if (group.warehousesInfo?.[id] || group.realWarehouseIds?.includes(id)) {
        return { warehouseTypeId, hash, group }
      }
    }
  }
}

/** dd.mm.yyyy -> yyyy-mm-dd */
export const toMinDate = (date?: string | null) => date
  ? date.replace(/(\d{2})\.(\d{2})\.(\d{4})/, '$3-$2-$1')
  : undefined
